import React from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import IVButton from '@invideoio/web-shared/components/Button';
import IVCard from '@invideoio/web-shared/components/Card';

import { BrowserProps, PosterType } from '../../../types';
import { getPosterURL } from '@invideoio/web-shared/utils/common';
import { FeaturedMediaCardType } from '../../../types';
import ImageComponent from '../ImageComponent/ImageComponent';
import TitleH2BlockGradient from '../Title/TitleH2BlockGradient';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { StateContext } from '@store/store';
import { useContext } from 'react';
import { toAppSigninUrl } from '@utils/app-signin';

interface WorkflowProps extends BrowserProps {
  platform: string;
  browser: string;
  featuredMediaCard: FeaturedMediaCardType;
}

const MediaFeaturedCard = (props: WorkflowProps) => {
  const { browser, featuredMediaCard } = props;
  const globalState = useContext(StateContext);
  const { platform } = globalState;

  const {
    plainHeading,
    highlightedHeading,
    featuredMedia,
    featuredMediaMobile,
    buttonText,
    CTAUrl,
  } = featuredMediaCard;
  const title = {
    plainHeading: plainHeading || '',
    highlightedHeading: highlightedHeading || '',
  };

  let poster: PosterType;
  let imageURL: string;
  if (
    (platform === PlatformType.Mobile || platform === PlatformType.Tablet) &&
    featuredMediaMobile
  ) {
    poster = featuredMediaMobile.desktop || {
      defaultURL: featuredMediaMobile.url,
    };

    imageURL = getPosterURL(poster, browser);
  } else {
    poster = featuredMedia.desktop || { defaultURL: featuredMedia.url };

    imageURL = getPosterURL(poster, browser);
  }

  return (
    <section
      id="iv-workflow"
      className={ivclass(
        'xl:iv-px-20 lg:iv-px-40 md:iv-px-25 sm:iv-px-20',
        'xl:iv-mt-200 iv-mt-150 sm:iv-mt-100',
        'iv-grid, iv-grid-cols-12',
      )}
    >
      {(plainHeading || highlightedHeading) && (
        <div
          className={ivclass(
            'iv-grid',
            'iv-mb-50 sm:iv-mb-30 md:iv-px-30 sm:iv-px-20',
          )}
        >
          <TitleH2BlockGradient
            heading={title}
            headingClass="iv-grid md:iv-mb-15 lg:iv-mb-15 xl:iv-mb-15 2xl:iv-mb-15"
            spanClass="iv-block md:iv-inline sm:iv-inline"
          />
        </div>
      )}

      <div
        className={ivclass(
          'iv-grid',
          'iv-gap-40 md:iv-gap-25 sm:iv-gap-25',
          'iv-grid-cols-1',
        )}
      >
        <IVCard>
          <div
            className={ivclass(
              'xl:iv-grid lg:iv-grid',
              'xl:iv-grid-cols-12 lg:iv-grid-cols-12',
              'sm:iv-gap-30',
              'md:iv-flex',
              'md:iv-h-full',
              'sm:iv-flex',
              'sm:iv-flex-col',
              'sm:iv-h-full',
            )}
          >
            <div
              className={ivclass(
                'xl:iv-col-span-12 lg:iv-col-span-12 sm:iv-col-span-12 sm:iv-order-1',
                'md:iv-flex-1',
              )}
            >
              <div
                className={ivclass(
                  'iv-relative',
                  'iv-flex iv-justify-center iv-h-auto',
                  'iv-h-auto',
                  'iv-overflow-hidden',
                )}
              >
                {imageURL && (
                  <ImageComponent
                    elementype="native"
                    className="iv-w-full iv-object-cover iv-object-center"
                    loading="lazy"
                    src={imageURL}
                    alt={`${plainHeading || 'Featured'} ${
                      highlightedHeading || 'media'
                    }`}
                  />
                )}
              </div>
            </div>
          </div>
        </IVCard>
        {CTAUrl && buttonText && (
          <div className={ivclass('iv-text-center', 'xl:iv-mt-20 iv-mt-20')}>
            <span
              className={ivclass(
                'iv-inline-flex iv-justify-center iv-items-center',
                'iv-cursor-pointer',
                'iv-rounded-full',
                'iv-border-2',
                'iv-px-25 sm:iv-px-15',
                'iv-h-52 sm:iv-h-28',
                'iv-text-body-2xl sm:iv-text-body-m',
                'iv-font-semibold',
                'iv-transition-all iv-duration-150',

                'iv-text-back-85 iv-border-back-85 hover:iv-text-grey-0 hover:iv-bg-black-85',
              )}
              onClick={() => {
                window.open(toAppSigninUrl(CTAUrl), '_blank', 'noopener');
              }}
            >
              {buttonText}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default MediaFeaturedCard;
